/**
 * File Upload Validation Utility
 *
 * Handles validation for credit report file uploads (HTML and PDF exports).
 *
 * SECURITY NOTE:
 * - HTML files are NOT rendered in browser
 * - HTML files are treated as parser input only
 * - All processing happens server-side
 */

// ============================================
// SUPPORTED FILE TYPES
// ============================================

/** Listed in import UI; HTML exports from these providers are supported at launch. */
export const CREDIT_REPORT_SUPPORTED_PROVIDERS_LINE =
  'Supported providers: Experian Portal Printable HTML (Experian, Equifax, TransUnion), IdentityIQ, CreditHeroScore, SmartCredit, MyScoreIQ, MyFreeScoreNow, and PrivacyGuard.';

/** User-facing copy when images or other unsupported files are chosen. */
export const CREDIT_REPORT_HTML_ONLY_MESSAGE =
  'Please upload the HTML or PDF export of your credit report from a supported provider (including Experian Portal Printable HTML for Experian, Equifax, or TransUnion). Image and screenshot uploads are not supported in this version.';

/** Extensions blocked client-side before any upload (explicit rejection message). */
export const BLOCKED_CREDIT_REPORT_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
  '.webp',
];

export const SUPPORTED_EXTENSIONS = {
  pdf: {
    extensions: ['.pdf'],
    mimeTypes: ['application/pdf'],
    label: 'PDF',
    importType: 'manual_pdf_upload',
    uploadAllowed: true,
  },
  html: {
    extensions: ['.html', '.htm'],
    mimeTypes: ['text/html', 'application/xhtml+xml'],
    label: 'HTML',
    importType: 'manual_html_upload',
    uploadAllowed: true,
  },
  mhtml: {
    extensions: ['.mhtml', '.mht'],
    mimeTypes: ['message/rfc822', 'multipart/related'],
    label: 'MHTML',
    importType: 'manual_mhtml_upload',
    optional: true,
    uploadAllowed: false,
  },
};

const uploadEligible = (typeConfig) =>
  typeConfig.uploadAllowed !== false && !typeConfig.optional;

// All accepted extensions flattened (credit report upload: HTML only)
export const ALL_ACCEPTED_EXTENSIONS = Object.values(SUPPORTED_EXTENSIONS)
  .filter(uploadEligible)
  .flatMap((type) => type.extensions);

// All accepted MIME types flattened
export const ALL_ACCEPTED_MIME_TYPES = Object.values(SUPPORTED_EXTENSIONS)
  .filter(uploadEligible)
  .flatMap((type) => type.mimeTypes);

// ============================================
// FILE SIZE LIMITS
// ============================================

export const FILE_SIZE_LIMITS = {
  maxSize: 70 * 1024 * 1024,
  maxSizeLabel: '70MB',
  minSize: 100,
  minSizeLabel: '100 bytes',
};

// ============================================
// VALIDATION FUNCTIONS
// ============================================

export const getFileExtension = (filename) => {
  if (!filename) return '';
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.substring(lastDot).toLowerCase();
};

/**
 * Detect file type from extension and MIME type.
 * Returns the type config or null if unsupported for upload.
 */
export const detectFileType = (file) => {
  if (!file) return null;

  const extension = getFileExtension(file.name);
  const mimeType = file.type?.toLowerCase() || '';

  for (const [typeKey, typeConfig] of Object.entries(SUPPORTED_EXTENSIONS)) {
    if (!uploadEligible(typeConfig)) continue;

    if (typeConfig.extensions.includes(extension)) {
      return {
        key: typeKey,
        ...typeConfig,
        detectedBy: 'extension',
      };
    }

    if (typeConfig.mimeTypes.includes(mimeType)) {
      return {
        key: typeKey,
        ...typeConfig,
        detectedBy: 'mimeType',
      };
    }
  }

  return null;
};

/**
 * Validate file for credit report HTML upload.
 * Returns { valid: boolean, error?: string, fileType?: object, importType?: string }
 */
export const validateReportFile = (file) => {
  if (!file) {
    return {
      valid: false,
      error: 'No file selected.',
    };
  }

  if (file.size > FILE_SIZE_LIMITS.maxSize) {
    return {
      valid: false,
      error: `File is too large. Maximum size is ${FILE_SIZE_LIMITS.maxSizeLabel}.`,
    };
  }

  if (file.size < FILE_SIZE_LIMITS.minSize) {
    return {
      valid: false,
      error: 'File appears to be empty or corrupted.',
    };
  }

  const extension = getFileExtension(file.name);
  if (BLOCKED_CREDIT_REPORT_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: CREDIT_REPORT_HTML_ONLY_MESSAGE,
    };
  }

  const fileType = detectFileType(file);

  if (!fileType) {
    return {
      valid: false,
      error: CREDIT_REPORT_HTML_ONLY_MESSAGE,
    };
  }

  return {
    valid: true,
    fileType,
    importType: fileType.importType,
  };
};

export const getAcceptString = () => {
  const extensions = ALL_ACCEPTED_EXTENSIONS.join(',');
  const mimeTypes = ALL_ACCEPTED_MIME_TYPES.join(',');
  return `${extensions},${mimeTypes}`;
};

export const getSupportedFormatsLabel = () => 'HTML or PDF';

export const getSupportedFormatsDetail = () => {
  return Object.values(SUPPORTED_EXTENSIONS)
    .filter(uploadEligible)
    .map((type) => `${type.label} (${type.extensions.join(', ')})`)
    .join(', ');
};

// ============================================
// IMPORT TYPE CLASSIFICATION
// ============================================

export const IMPORT_TYPES = {
  credential_import: {
    label: 'Secure Login Import',
    description: 'Imported via provider credentials',
  },
  manual_pdf_upload: {
    label: 'PDF Upload',
    description: 'Manually uploaded PDF report',
  },
  manual_html_upload: {
    label: 'HTML Upload',
    description: 'Manually uploaded HTML report source',
  },
  manual_mhtml_upload: {
    label: 'MHTML Upload',
    description: 'Manually uploaded MHTML archive',
  },
  supported_source_upload: {
    label: 'Source Upload',
    description: 'Uploaded supported report source file',
  },
};

export const getImportTypeLabel = (importType) => {
  return IMPORT_TYPES[importType]?.label || 'Unknown Import';
};

export default {
  SUPPORTED_EXTENSIONS,
  ALL_ACCEPTED_EXTENSIONS,
  ALL_ACCEPTED_MIME_TYPES,
  FILE_SIZE_LIMITS,
  BLOCKED_CREDIT_REPORT_EXTENSIONS,
  CREDIT_REPORT_HTML_ONLY_MESSAGE,
  CREDIT_REPORT_SUPPORTED_PROVIDERS_LINE,
  getFileExtension,
  detectFileType,
  validateReportFile,
  getAcceptString,
  getSupportedFormatsLabel,
  getSupportedFormatsDetail,
  IMPORT_TYPES,
  getImportTypeLabel,
};
